// Shared bilingual WhatsApp summary generator — used by the admin dashboard.
// Logic mirrors the customer-facing WhatsApp summary page so admin sees identical text.

import { PROFILE_QUESTIONS, RED_FLAG_QUESTIONS, MODULES, CONCERN_MODULE_MAP } from '../data/modules';
import { CONCERN_OPTIONS, CONCERN_RESULTS, CONCERN_FOLLOWUPS, GOAL_CONFIRM, getAllConcernIds } from '../data/combinedConcerns';

const findOptionLabel = (question, optionId, lang) => {
  if (!question || optionId === undefined || optionId === null) return optionId ?? '';
  if (question.type === 'slider') return String(optionId);
  if (question.type === 'text') return String(optionId);
  const opt = question.options?.find((o) => o.id === optionId);
  return opt ? (lang === 'zh' ? opt.zh : opt.en) : String(optionId ?? '');
};

const findProfileQ = (id) => PROFILE_QUESTIONS.find((q) => q.id === id);
const findRedFlagQ = (id) => RED_FLAG_QUESTIONS.find((q) => q.id === id);

const concernLabel = (cid, lang) => {
  const opt = CONCERN_OPTIONS.find((o) => o.id === cid);
  if (!opt) return cid;
  return lang === 'zh' ? opt.zh : opt.en;
};

const concernsLine = (submission, lang) => {
  const ids = getAllConcernIds(submission);
  if (ids.length === 0) {
    // Legacy main_concern only
    return findOptionLabel(findProfileQ('main_concern'), submission.main_concern, lang) || '-';
  }
  const [primary, ...related] = ids;
  const primaryLabel = concernLabel(primary, lang);
  if (related.length === 0) return primaryLabel;
  const relatedLabels = related.map((r) => concernLabel(r, lang)).join(', ');
  return lang === 'zh'
    ? `${primaryLabel}（相关：${relatedLabels}）`
    : `${primaryLabel} (related: ${relatedLabels})`;
};

const followupLines = (submission, lang) => {
  const fu = submission.concern_followups || {};
  const out = [];
  for (const cid of getAllConcernIds(submission)) {
    const def = CONCERN_FOLLOWUPS[cid];
    const answers = fu[cid] || {};
    if (!def) continue;
    const yesQs = def.questions.filter((q) => answers[q.id] === 'yes');
    if (yesQs.length === 0) continue;
    const label = concernLabel(cid, lang);
    const list = yesQs.map((q) => (lang === 'zh' ? q.zh : q.en)).join(' / ');
    out.push(lang === 'zh' ? `· ${label} → 是: ${list}` : `· ${label} → Yes: ${list}`);
  }
  return out;
};

const goalLine = (submission, lang) => {
  if (!submission.goal_confirmation) return null;
  const opt = GOAL_CONFIRM.options.find((o) => o.id === submission.goal_confirmation);
  if (!opt) return null;
  return lang === 'zh' ? `确认目标：${opt.zh}` : `Confirmed goal: ${opt.en}`;
};

const resultTitleLine = (submission, lang) => {
  const ids = getAllConcernIds(submission);
  if (ids.length > 0) {
    const cid = ids[0];
    const r = CONCERN_RESULTS[cid];
    if (r) return lang === 'zh' ? r.title_zh : r.title_en;
  }
  // Legacy path
  const moduleId = submission.module_id || submission.result_type || CONCERN_MODULE_MAP[submission.main_concern];
  const mod = moduleId ? MODULES[moduleId] : null;
  if (mod) return lang === 'zh' ? mod.result.title_zh : mod.result.title_en;
  return lang === 'zh' ? '请联系顾问' : 'Please contact a consultant';
};

export const buildWhatsAppSummary = (submission, lang) => {
  if (!submission) return '';
  const rf = submission.red_flag_answers || {};
  const fu = followupLines(submission, lang);
  const goal = goalLine(submission, lang);

  if (lang === 'zh') {
    const lines = [
      'Hi Soo, 我刚完成哺乳 self-check，这是我的情况：',
      '',
      `产后：${findOptionLabel(findProfileQ('postpartum_age'), submission.postpartum_age, 'zh') || '-'}`,
      `第几胎：${findOptionLabel(findProfileQ('birth_order'), submission.birth_order, 'zh') || '-'}`,
      `目前位置：${findOptionLabel(findProfileQ('location_type'), submission.location_type, 'zh') || '-'}`,
      `地区/月子中心：${submission.location_text || '-'}`,
      `喂养方式：${findOptionLabel(findProfileQ('feeding_method'), submission.feeding_method, 'zh') || '-'}`,
      `主要问题：${concernsLine(submission, 'zh')}`,
    ];
    if (goal) lines.push(goal);
    lines.push(
      `疼痛分数：${rf.pain_score ?? 0}/10`,
      `是否发烧：${findOptionLabel(findRedFlagQ('fever'), rf.fever, 'zh') || '-'}`,
      `是否红肿热痛：${findOptionLabel(findRedFlagQ('redness'), rf.redness, 'zh') || '-'}`,
      `宝宝情况：${findOptionLabel(findRedFlagQ('baby_concern'), rf.baby_concern, 'zh') || '-'}`,
    );
    if (fu.length > 0) {
      lines.push('关键症状：');
      lines.push(...fu);
    }
    lines.push(
      `系统初步结果：${resultTitleLine(submission, 'zh')}`,
      `风险等级：${submission.risk_level === 'red' ? '红色 Red' : submission.risk_level === 'yellow' ? '黄色 Yellow' : '绿色 Green'}`,
      `客户首选顾问：${submission.preferred_consultant || '-'}`,
    );
    return lines.join('\n');
  }
  const lines = [
    'Hi Soo, the customer completed the breastfeeding self-check. Here is the summary:',
    '',
    `Postpartum: ${findOptionLabel(findProfileQ('postpartum_age'), submission.postpartum_age, 'en') || '-'}`,
    `Birth order: ${findOptionLabel(findProfileQ('birth_order'), submission.birth_order, 'en') || '-'}`,
    `Current location: ${findOptionLabel(findProfileQ('location_type'), submission.location_type, 'en') || '-'}`,
    `Area/centre: ${submission.location_text || '-'}`,
    `Feeding method: ${findOptionLabel(findProfileQ('feeding_method'), submission.feeding_method, 'en') || '-'}`,
    `Main concern: ${concernsLine(submission, 'en')}`,
  ];
  if (goal) lines.push(goal);
  lines.push(
    `Pain score: ${rf.pain_score ?? 0}/10`,
    `Fever: ${findOptionLabel(findRedFlagQ('fever'), rf.fever, 'en') || '-'}`,
    `Red/hot/swollen: ${findOptionLabel(findRedFlagQ('redness'), rf.redness, 'en') || '-'}`,
    `Baby: ${findOptionLabel(findRedFlagQ('baby_concern'), rf.baby_concern, 'en') || '-'}`,
  );
  if (fu.length > 0) {
    lines.push('Key symptoms:');
    lines.push(...fu);
  }
  lines.push(
    `Initial result: ${resultTitleLine(submission, 'en')}`,
    `Risk level: ${submission.risk_level === 'red' ? 'Red' : submission.risk_level === 'yellow' ? 'Yellow' : 'Green'}`,
    `Customer preferred consultant: ${submission.preferred_consultant || '-'}`,
  );
  return lines.join('\n');
};

// Render structured "grouped self-check answers" arrays for the admin drawer.
export const buildGroupedAnswers = (submission, lang) => {
  if (!submission) return { profile: [], redFlags: [], module: null };
  const rf = submission.red_flag_answers || {};
  const ma = submission.module_answers || {};

  const profile = [
    { label: lang === 'zh' ? '填写者' : 'Filled in by', value: findOptionLabel(findProfileQ('user_role'), submission.user_role, lang) },
    { label: lang === 'zh' ? '产后' : 'Postpartum', value: findOptionLabel(findProfileQ('postpartum_age'), submission.postpartum_age, lang) },
    { label: lang === 'zh' ? '第几胎' : 'Birth order', value: findOptionLabel(findProfileQ('birth_order'), submission.birth_order, lang) },
    { label: lang === 'zh' ? '目前位置' : 'Location type', value: findOptionLabel(findProfileQ('location_type'), submission.location_type, lang) },
    { label: lang === 'zh' ? '地区/月子中心' : 'Area / centre', value: submission.location_text || '-' },
    { label: lang === 'zh' ? '喂养方式' : 'Feeding method', value: findOptionLabel(findProfileQ('feeding_method'), submission.feeding_method, lang) },
    { label: lang === 'zh' ? '主要问题' : 'Main concern', value: concernsLine(submission, lang) },
  ];
  const goal = goalLine(submission, lang);
  if (goal) profile.push({ label: lang === 'zh' ? '确认目标' : 'Confirmed goal', value: goal.replace(/^[^:：]+[:：]\s*/, '') });

  const redFlags = [
    { label: lang === 'zh' ? '发烧' : 'Fever', value: findOptionLabel(findRedFlagQ('fever'), rf.fever, lang) },
    { label: lang === 'zh' ? '发冷/酸痛' : 'Chills / body ache', value: findOptionLabel(findRedFlagQ('chills'), rf.chills, lang) },
    { label: lang === 'zh' ? '红肿热痛' : 'Red/hot/swollen', value: findOptionLabel(findRedFlagQ('redness'), rf.redness, lang) },
    { label: lang === 'zh' ? '疼痛分数' : 'Pain score', value: `${rf.pain_score ?? 0} / 10` },
    { label: lang === 'zh' ? '宝宝情况' : 'Baby concern', value: findOptionLabel(findRedFlagQ('baby_concern'), rf.baby_concern, lang) },
  ];

  let moduleSection = null;
  const concernIds = getAllConcernIds(submission);
  if (concernIds.length > 0) {
    const fu = submission.concern_followups || {};
    const answers = [];
    for (const cid of concernIds) {
      const def = CONCERN_FOLLOWUPS[cid];
      if (!def) continue;
      const label = concernLabel(cid, lang);
      for (const q of def.questions) {
        const ans = (fu[cid] || {})[q.id];
        const opt = q.options.find((o) => o.id === ans);
        answers.push({
          label: `${label} · ${lang === 'zh' ? q.zh : q.en}`,
          value: opt ? (lang === 'zh' ? opt.zh : opt.en) : '-',
        });
      }
    }
    moduleSection = {
      title: lang === 'zh' ? '问题详情（综合）' : 'Concern details (combined)',
      result: resultTitleLine(submission, lang),
      answers,
    };
  } else {
    // Legacy module-answers fallback
    const moduleId = submission.module_id || submission.result_type || CONCERN_MODULE_MAP[submission.main_concern];
    if (moduleId && MODULES[moduleId]) {
      const mod = MODULES[moduleId];
      const answers = mod.questions.map((q) => ({
        label: lang === 'zh' ? q.zh : q.en,
        value: q.type === 'choice'
          ? findOptionLabel(q, ma[q.id], lang)
          : (ma[q.id] !== undefined ? String(ma[q.id]) : '-'),
      }));
      moduleSection = {
        title: lang === 'zh' ? mod.title_zh : mod.title_en,
        result: lang === 'zh' ? mod.result.title_zh : mod.result.title_en,
        answers,
      };
    }
  }

  return { profile, redFlags, module: moduleSection };
};
